import * as React from 'react';
import { View, Text, Animated, Platform, FlatList } from 'react-native';
import { Card, ScrollView } from '@constants/Themed';
import styles from '../../../stylesheet';
import * as WebBrowser from 'expo-web-browser';
import { LinearGradient } from 'expo-linear-gradient';
import FlipCard from 'react-native-flip-card';
import DynamicPlanInfo from './DynamicPlanInfo';
import DynamicContentInfo from './components/DynamicContentInfo';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import BlogItem from './components/BlogItem';
import LottieView from '@utils/lottieAnimation';
import MarkDown from 'react-native-markdown-display';
import { rules } from '@utils/stringUtils/markDownRules';
import { Separator, SizableText, Tabs, TabsContentProps, XStack, YStack } from 'tamagui';
import { uniqueId } from 'lodash';
import { useRef, useState } from 'react';
import useChangeLogQuery from '@hooks/fetchChangeLog';
import ErrorBoundary from '@utils/ErrorBoundary';
import { AntDesign } from '@expo/vector-icons';

// Web-specific CSS isolation for markdown
const webMarkdownContainerStyle = Platform.OS === 'web' ? {
  // Isolate from global CSS
  isolation: 'isolate' as any,
  contain: 'layout style' as any,
  position: 'relative' as any,
  // Override global resets
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '1.4',
  color: 'inherit'
} : {};

function learnMorePress() {
  WebBrowser.openBrowserAsync(
    'https://smartsaas.co.uk'
  )
}
function privacyPolicy() {
  WebBrowser.openBrowserAsync(
    'https://smartsaas.co.uk/privacy-policy'
  )
}
function termsOfService() {
  WebBrowser.openBrowserAsync(
    'https://smartsaas.co.uk/terms-and-conditions/'
  )
}

function handleGoToBlog() {
  WebBrowser.openBrowserAsync(
    'https://smartsaas.co.uk/posts'
  )
}

// Safe Markdown Component with complete style isolation
interface SafeMarkDownProps {
  content: string;
  rules: any;
}

// Create a completely isolated markdown renderer
const IsolatedMarkdownContainer = ({ children }: { children: React.ReactNode }) => {
  if (Platform.OS === 'web') {
    // Inject CSS to completely reset and isolate markdown content
    const isolatedStyle = `
      .markdown-isolated-container {
        all: initial !important;
        font-family: Inter, sans-serif !important;
        font-size: 14px !important;
        line-height: 1.4 !important;
        color: inherit !important;
        background-color: transparent !important;
        position: relative !important;
        width: 100% !important;
        height: 100% !important;
        overflow: auto !important;
        isolation: isolate !important;
        contain: layout style size !important;
        display: block !important;
        box-sizing: border-box !important;
      }
      
      .markdown-isolated-container * {
        all: unset !important;
        display: revert !important;
        box-sizing: border-box !important;
        font-family: inherit !important;
        color: inherit !important;
      }
      
      .markdown-isolated-container p {
        display: block !important;
        margin: 8px 0 !important;
        font-size: 14px !important;
        line-height: 1.4 !important;
      }
      
      .markdown-isolated-container h1 {
        display: block !important;
        font-size: 24px !important;
        font-weight: bold !important;
        margin: 16px 0 !important;
      }
      
      .markdown-isolated-container h2 {
        display: block !important;
        font-size: 20px !important;
        font-weight: bold !important;
        margin: 12px 0 !important;
      }
      
      .markdown-isolated-container h3 {
        display: block !important;
        font-size: 18px !important;
        font-weight: 600 !important;
        margin: 10px 0 !important;
      }
      
      .markdown-isolated-container ul, .markdown-isolated-container ol {
        display: block !important;
        margin: 8px 0 !important;
        padding-left: 20px !important;
        list-style: revert !important;
      }
      
      .markdown-isolated-container li {
        display: list-item !important;
        margin: 2px 0 !important;
        font-size: 14px !important;
        line-height: 1.4 !important;
      }
    `;

    return (
      <div>
        <style dangerouslySetInnerHTML={{ __html: isolatedStyle }} />
        <div className="markdown-isolated-container">
          {children}
        </div>
      </div>
    );
  }

  // Native: just use View
  return (
    <View style={{ flex: 1, position: 'relative' }}>
      {children}
    </View>
  );
};

// Alternative: Use a custom renderer that doesn't rely on react-native-markdown-display
const CustomMarkdownRenderer = ({ content }: { content: string }) => {
  const parseSimpleMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];

    lines.forEach((line, index) => {
      if (line.trim() === '') {
        elements.push(<View key={`spacer-${index}`} style={{ height: 8 }} />);
      } else if (line.startsWith('# ')) {
        elements.push(
          <Text key={index} style={{ fontSize: 24, fontWeight: 'bold', marginVertical: 16, color: 'inherit' }}>
            {line.replace('# ', '')}
          </Text>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <Text key={index} style={{ fontSize: 20, fontWeight: 'bold', marginVertical: 12, color: 'inherit' }}>
            {line.replace('## ', '')}
          </Text>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <Text key={index} style={{ fontSize: 18, fontWeight: '600', marginVertical: 10, color: 'inherit' }}>
            {line.replace('### ', '')}
          </Text>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        elements.push(
          <View key={index} style={{ flexDirection: 'row', marginVertical: 2, paddingLeft: 16 }}>
            <Text style={{ marginRight: 8, color: 'inherit' }}>•</Text>
            <Text style={{ flex: 1, fontSize: 14, lineHeight: 20, color: 'inherit' }}>
              {line.replace(/^[-*] /, '')}
            </Text>
          </View>
        );
      } else {
        elements.push(
          <Text key={index} style={{ fontSize: 14, lineHeight: 20, marginVertical: 4, color: 'inherit' }}>
            {line}
          </Text>
        );
      }
    });

    return elements;
  };

  return (
    <View style={{ flex: 1, padding: 0 }}>
      {parseSimpleMarkdown(content)}
    </View>
  );
};

const SafeMarkDown: React.FC<SafeMarkDownProps> = React.memo(({ content, rules }) => {
  // Validate content before rendering
  if (!content || typeof content !== 'string' || content.trim() === '') {
    return (
      <View style={{ padding: 20, alignItems: 'center' }}>
        <Text style={{ fontSize: 16, color: '#666', textAlign: 'center' }}>
          No changelog content available
        </Text>
      </View>
    );
  }

  // Clean the content
  const cleanContent = content
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\t/g, '  ') // Replace tabs with spaces
    .trim();

  // Use custom renderer to completely avoid global CSS conflicts
  if (Platform.OS === 'web') {
    return (
      <IsolatedMarkdownContainer>
        <CustomMarkdownRenderer content={cleanContent} />
      </IsolatedMarkdownContainer>
    );
  }

  // For native, use the original markdown renderer
  try {
    return (
      <View style={{ flex: 1, position: 'relative' }}>
        <MarkDown 
          rules={rules} 
          style={{ 
            body: {
              fontSize: 14,
              lineHeight: 20,
              fontFamily: 'Inter, sans-serif',
              color: 'inherit',
              margin: 0,
              padding: 0,
              backgroundColor: 'transparent'
            },
            paragraph: {
              marginTop: 8,
              marginBottom: 8,
              marginLeft: 0,
              marginRight: 0,
              fontSize: 14,
              lineHeight: 20,
              color: 'inherit',
              fontFamily: 'Inter, sans-serif'
            },
            heading1: {
              fontSize: 24,
              fontWeight: 'bold',
              marginTop: 16,
              marginBottom: 16,
              marginLeft: 0,
              marginRight: 0,
              color: 'inherit',
              fontFamily: 'Inter, sans-serif'
            },
            heading2: {
              fontSize: 20,
              fontWeight: 'bold',
              marginTop: 12,
              marginBottom: 12,
              marginLeft: 0,
              marginRight: 0,
              color: 'inherit',
              fontFamily: 'Inter, sans-serif'
            },
            heading3: {
              fontSize: 18,
              fontWeight: '600',
              marginTop: 10,
              marginBottom: 10,
              marginLeft: 0,
              marginRight: 0,
              color: 'inherit',
              fontFamily: 'Inter, sans-serif'
            },
            code_inline: {
              backgroundColor: '#f5f5f5',
              paddingTop: 2,
              paddingBottom: 2,
              paddingLeft: 4,
              paddingRight: 4,
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: 13
            },
            bullet_list: {
              marginTop: 8,
              marginBottom: 8,
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: 20
            },
            ordered_list: {
              marginTop: 8,
              marginBottom: 8,
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: 20
            },
            list_item: {
              marginTop: 4,
              marginBottom: 4,
              marginLeft: 0,
              marginRight: 0,
              fontSize: 14,
              lineHeight: 20,
              color: 'inherit'
            }
          }}
        >
          {cleanContent}
        </MarkDown>
      </View>
    );
  } catch (error) {
    console.warn('Markdown rendering error:', error);
    return (
      <View style={{ padding: 20, alignItems: 'center' }}>
        <Text style={{ fontSize: 16, color: '#e74c3c', textAlign: 'center' }}>
          Error rendering markdown content
        </Text>
        <Text style={{ fontSize: 12, color: '#999', textAlign: 'center', marginTop: 5 }}>
          {error?.message || 'Unknown error'}
        </Text>
      </View>
    );
  }
});

const TabsContent = (props: TabsContentProps) => {
  return (
    <Tabs.Content
      backgroundColor="$transparent"
      key="tab3"
      padding="$2"
      alignItems="center"
      justifyContent="center"
      flex={1}
      borderColor="$transparent"
      borderRadius={0}
      borderTopLeftRadius={0}
      borderTopRightRadius={0}
      borderWidth={0}
      minHeight={"100%"}
      {...props}
    >
      {props.children}
    </Tabs.Content>
  )
}

const _renderItem = ({ item, index }) => {
  return (
    <BlogItem item={item} index={index} />
  )
}

const DynamicContent = ({ registerModalState, selectedPlanState }) => {
  const [postData, setPostData] = React.useState([]);
  const [journal, setJournal] = React.useState(false);
  const queryClient = useQueryClient();
  const [changelogContent, setChangelogContent] = React.useState('');
  const changeLogData = useChangeLogQuery();
  const [completeScrollBarHeight, setCompleteScrollBarHeight] = useState(1);
  const [visibleScrollBarHeight, setVisibleScrollBarHeight] = useState(0);
  const [fetchingPosts, setFetchingPosts] = useState(false);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [columns, setColumns] = useState(1);
  const modalRef = useRef(null);

  React.useEffect(() => {
    if (changeLogData?.data && typeof changeLogData.data === 'string') {
      try {
        // Validate that the data is proper markdown/text
        const cleanData = changeLogData.data.trim();
        if (cleanData.length > 0) {
          setChangelogContent(cleanData);
        } else {
          console.warn('Changelog data is empty');
          setChangelogContent('');
        }
      } catch (error) {
        console.error('Error processing changelog data:', error);
        setChangelogContent('');
      }
    } else {
      setChangelogContent('');
    }
  }, [changeLogData]);

  const scrollIndicatorSize =
    completeScrollBarHeight > visibleScrollBarHeight
      ? (visibleScrollBarHeight / completeScrollBarHeight) * visibleScrollBarHeight
      : visibleScrollBarHeight;

  const scrollIndicator = useRef(new Animated.Value(0)).current;
  const difference =
    visibleScrollBarHeight > scrollIndicatorSize
      ? visibleScrollBarHeight - scrollIndicatorSize
      : 1;

  const scrollIndicatorPosition = Animated.multiply(
    scrollIndicator,
    visibleScrollBarHeight / completeScrollBarHeight
  ).interpolate({
    inputRange: [0, difference],
    outputRange: [0, difference],
    extrapolate: 'clamp'
  });


  const { status, data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['postData'],
    queryFn: async () => {
      setFetchingPosts(true);
      try {
        const proxyUrl = 'https://corsproxy.io/?';
        const targetUrl = `${process.env.EXPO_PUBLIC_WP_URL}/wp-json/wp/v2/posts`;
        if (!targetUrl) {
          return [];
        }
        // prepend a CORS proxy to ignore CORS for this request
        const fetchUrl = proxyUrl + encodeURIComponent(targetUrl);
        const response = await fetch(fetchUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
        });

        if (response && response.status === 200) {
          const json = await response.json();
          setJournal(true);
          return json;
        }

        return [];
      } catch (err) {
        return [];
      } finally {
        setFetchingPosts(false);
      }
    },
    retry: 20,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  })


  return (
    <View style={{ flex: 1.5 }}>
      <View style={{ flexDirection: 'column', flex: 1 }}>
        <DynamicContentInfo modalRef={modalRef} />
        <Separator />
          <FlipCard
          style={{ flex: 1, flexDirection: 'column' }}
            flipVertical={true}
            flip={registerModalState}
            clickable={false}
          >
          <View style={[styles.face, { flex: 1, gap: 20 }]}>
            <YStack style={{ flex: 10 }}>
              <Tabs
                defaultValue={'journal'}
                borderWidth={0}
                flexDirection='column'
                flex={1}
              >
                <Tabs.List
                  borderWidth={0}
                  disablePassBorderRadius="end"
                  borderRadius={0}
                  aria-label="Switch between our journal and log"
                  style={{
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 8,
                    elevation: 3,
                  }}
                >
                  <Tabs.Tab
                    value="journal"
                    flex={1}
                    borderRadius={0}
                    backgroundColor="transparent"
                    paddingVertical="$3"
                    gap="$2"
                    flexDirection='row'
                    borderWidth={0}
                    onPress={() => setJournal(true)}
                    pressStyle={{
                      backgroundColor: '$blue4',
                      transform: [{ scale: 0.98 }],
                    }}
                    focusStyle={{
                      backgroundColor: '$blue5',
                    }}
                    hoverStyle={{
                      backgroundColor: '$blue3',
                    }}
                    style={{
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <AntDesign
                      name="book"
                      size={20}
                      color={journal ? "#3B82F6" : "#6B7280"}
                    />
                    <SizableText
                      fontFamily="$body"
                      fontSize="$3"
                      fontWeight={journal ? "600" : "400"}
                      color={journal ? "$blue10" : "$grey10"}
                    >
                      Journal
                    </SizableText>
                  </Tabs.Tab>

                  <Tabs.Tab
                    value="changelog"
                    flex={1}
                    borderRadius={0}
                    backgroundColor="transparent"
                    paddingVertical="$3"
                    paddingHorizontal="$4"
                    gap="$2"
                    flexDirection='row'
                    borderWidth={0}
                    onPress={() => setJournal(false)}
                    pressStyle={{
                      backgroundColor: '$green4',
                      transform: [{ scale: 0.98 }],
                    }}
                    style={{
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <AntDesign
                      name="file"
                      size={20}
                      color={!journal ? "#10B981" : "#6B7280"}
                    />
                    <SizableText
                      fontFamily="$body"
                      fontSize="$3"
                      fontWeight={!journal ? "600" : "400"}
                      color={!journal ? "$green10" : "$grey10"}
                    >
                      Changelog
                    </SizableText>
                  </Tabs.Tab>
                </Tabs.List>
                <Card
                  alignSelf='stretch'
                  padding="$0"
                  style={{
                    elevation: 5,
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                    shadowOpacity: 0
                  }}
                >
                  <Tabs.Content
                    key={'journal-tab'}
                    value="journal"
                    padding="$0"
                    flex={1}
                  >
                  {
                    isLoading || isFetching ? 
                        <View style={{
                          flex: 1,
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: 40,
                          borderRadius: 20
                        }}>
                          <LottieView
                            source={require('@assets/animations/spin-leaf-loader.json')}
                            autoPlay
                            loop
                            style={{ width: 100, height: 100, alignSelf: "center" }}
                          />
                        </View>
                    :
                        <FlatList
                          key={uniqueId(columns.toString())}
                          showsVerticalScrollIndicator={false}
                          scrollEnabled
                          style={{ maxHeight: "80vh", backgroundColor: 'white' }}
                          keyExtractor={item => item.id || 'journal' + uniqueId()}
                          data={data}
                          maxToRenderPerBatch={3}
                          initialNumToRender={3}
                          contentContainerStyle={{
                            gap: 15,
                            padding: 20,
                            paddingBottom: 40,
                            backgroundColor: 'white'
                          }}
                          nestedScrollEnabled
                          renderItem={_renderItem}
                          ItemSeparatorComponent={() => (<View style={{ minHeight: 20, backgroundColor: 'white' }}></View>)}
                          ListEmptyComponent={() => !fetchingPosts && (<View style={[styles.centerEverything, { flex: 1, flexDirection: 'row', backgroundColor: 'white' }]}>
                      <LottieView autoPlay style={{ width: 80, margin: 20 }} source={require('../../../assets/animations/errorIcon.json')} loop={true} speed={1} />
                      <Text style={{ verticalAlign: 'middle', paddingVertical: 40, textAlign: "center" }}>Could not download recent posts, please check your network connection</Text>
                    </View>)}
                  />
                  }
                </Tabs.Content>
                  <Tabs.Content
                    key={"changelog-tab"}
                    borderRadius="$6"
                    flex={1}
                    value="changelog"
                    backgroundColor="transparent"
                    padding="$0"
                  >
                  <View style={{ flex: 1 }} key={"changelog-tab"}>
                    {
                      changeLogData?.isLoading ? (
                        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                          <LottieView 
                            source={require('@assets/animations/spin-leaf-loader.json')} 
                            autoPlay 
                            loop 
                            style={{ width: 100, height: 100, alignSelf: "center" }} 
                          />
                          <Text style={{ marginTop: 20, fontSize: 16, color: '#666' }}>
                            Loading changelog...
                          </Text>
                        </View>
                      ) : changeLogData?.error ? (
                        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                          <LottieView 
                            autoPlay 
                            style={{ width: 80, margin: 20 }} 
                            source={require('../../../assets/animations/errorIcon.json')} 
                            loop={true} 
                            speed={1} 
                          />
                          <Text style={{ fontSize: 16, color: '#e74c3c', textAlign: 'center', marginBottom: 10 }}>
                            Failed to load changelog
                          </Text>
                          <Text style={{ fontSize: 14, color: '#666', textAlign: 'center' }}>
                            Please check your network connection and try again
                          </Text>
                        </View>
                      ) : changeLogData?.data && changelogContent ? (
                        <View style={{ flex: 25, flexDirection: 'row', paddingHorizontal: 5, marginBottom: 100 }}>
                          <ScrollView
                                  style={{
                                    flex: 1,
                                    marginBottom: 50,
                                    // Isolate scrollview from global styles
                                    position: 'relative',
                                    backgroundColor: 'transparent'
                                  }}
                                  contentContainerStyle={{
                                    padding: 30,
                                    // Prevent style bleeding
                                    minHeight: '100%',
                                    flexGrow: 1
                                  }}
                            showsVerticalScrollIndicator={false}
                                  // Prevent global CSS from affecting scrolling
                                  nestedScrollEnabled={true}
                          >
                            <SafeMarkDown content={changelogContent} rules={rules} />
                          </ScrollView>
                            <View
                            style={{
                              flex: 1,
                              maxHeight: visibleScrollBarHeight,
                              maxWidth: 6,
                              width: 6,
                              backgroundColor: 'grey',
                              opacity: .6,
                              borderRadius: 8,
                              overflow: 'hidden',
                              left: 10
                            }}
                            >
                            <Animated.View
                              style={{
                              width: 6,
                              borderRadius: 8,
                              padding: 2,
                              backgroundColor: '#58BFEA',
                              maxHeight: scrollIndicatorSize,
                              transform: [{ translateY: scrollIndicatorPosition }]
                              }}
                            />
                            </View>
                        </View>
                      ) : (
                        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                          <LottieView 
                            autoPlay 
                            style={{ width: 80, margin: 20 }} 
                            source={require('../../../assets/animations/errorIcon.json')} 
                            loop={true} 
                            speed={1} 
                          />
                          <Text style={{ fontSize: 16, color: '#666', textAlign: 'center' }}>
                            No changelog content available
                          </Text>
                        </View>
                      )
                    }
                  </View>
                </Tabs.Content>
                </Card>
              </Tabs>
            </YStack>
            </View>
            {/*Card Flips Here!*/}
            <LinearGradient
              colors={['rgba(255,255,255,0.8)', 'transparent']}
              style={[styles.back, { flex: 1, alignSelf: 'stretch', paddingVertical: 20, paddingHorizontal: 20, borderRadius: 20 }]}
            >
              {
                selectedPlanState ?
                <DynamicPlanInfo selectedPlan={selectedPlanState} verticalTable={false} />
                :
                  <Text adjustsFontSizeToFit >Please select a plan to get more details</Text>
              }
              <Text adjustsFontSizeToFit >Sign up for our free 14 day trial today and experience what SmartSaaS can do for your business</Text>
              <Text adjustsFontSizeToFit >No Credit Card Required!</Text>
            </LinearGradient>
          </FlipCard>
      </View>
    </View >
  );
};

export default DynamicContent;